import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LabelLayout, LabelTextRegion } from '../../../../core/models/label-layout.model';

/** 96 dpi — matches browser default screen resolution */
const PX_PER_MM = 96 / 25.4;
const MM_PER_PT = 0.3528;
const TEXT_LINE_HEIGHT = 1.15;

type EditableElementId = 'code' | 'checkpointName' | 'formName' | 'serialNumber';
type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface BoundsMm {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DragSession {
  pointerId: number;
  elementId: EditableElementId;
  mode: 'move' | 'resize';
  handle?: ResizeHandle;
  startPointer: { x: number; y: number };
  startBounds: BoundsMm;
}

export interface LabelPreviewPatch {
  qrX?: number;
  qrY?: number;
  qrWidth?: number;
  qrHeight?: number;
  checkpointNameX?: number;
  checkpointNameY?: number;
  checkpointNameWidth?: number;
  checkpointNameHeight?: number;
  checkpointNameFontSize?: number;
  formNameX?: number;
  formNameY?: number;
  formNameWidth?: number;
  formNameHeight?: number;
  formNameFontSize?: number;
  serialNumberX?: number;
  serialNumberY?: number;
  serialNumberWidth?: number;
  serialNumberHeight?: number;
  serialNumberFontSize?: number;
}

/**
 * Renders a scaled-down preview of a single label showing the
 * QR code region and text regions based on the layout configuration.
 * Used inside grid cells and layout selector cards.
 *
 * Strategy: render content at its true physical pixel size (96 dpi), then
 * use transform: scale() to shrink it to fit the available container space.
 * This means font sizes are plain CSS pt — identical to how a print renderer
 * treats them — so wrapping occurs at exactly the same character positions.
 */
@Component({
  selector: 'app-label-preview',
  imports: [],
  templateUrl: './label-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './label-preview.component.css',
})
export class LabelPreviewComponent implements AfterViewInit, OnDestroy {
  @Input() layout!: LabelLayout;
  @Input() checkpointName = '';
  @Input() formName = '';
  @Input() showCode = true;
  @Input() showCheckpointName = true;
  @Input() showFormName = true;
  @Input() serialNumber = '';
  @Input() showSerialNumber = true;
  @Input() selected = false;
  @Input() checkpointNameFontSize = 10;
  @Input() formNameFontSize = 8;
  @Input() serialNumberFontSize = 8;
  @Input() fontFamily = 'helvetica';
  /** Code type: 'qr' (default) or 'barcode' */
  @Input() codeType: 'qr' | 'barcode' = 'qr';
  /** Show debug outlines around label regions */
  @Input() debug = false;
  /** Physical label width in mm */
  @Input() labelWidthMm = 63.5;
  /** Physical label height in mm */
  @Input() labelHeightMm = 25.4;

  // Optional position overrides (mm from label top-left origin; if undefined, falls back to layout config)
  @Input() qrX?: number;
  @Input() qrY?: number;
  @Input() qrWidth?: number;
  @Input() qrHeight?: number;
  @Input() checkpointNameX?: number;
  @Input() checkpointNameY?: number;
  @Input() checkpointNameWidth?: number;
  @Input() checkpointNameHeight?: number;
  @Input() formNameX?: number;
  @Input() formNameY?: number;
  @Input() formNameWidth?: number;
  @Input() formNameHeight?: number;
  @Input() serialNumberX?: number;
  @Input() serialNumberY?: number;
  @Input() serialNumberWidth?: number;
  @Input() serialNumberHeight?: number;
  @Input() editable = false;

  @Output() boundsChange = new EventEmitter<LabelPreviewPatch>();

  private el = inject(ElementRef<HTMLElement>);
  private cdr = inject(ChangeDetectorRef);
  private resizeObserver?: ResizeObserver;
  private dragSession: DragSession | null = null;
  private readonly minSizeMm = 4;

  scale = signal(1);
  selectedElement = signal<EditableElementId | null>(null);
  hoveredElement = signal<EditableElementId | null>(null);

  get naturalWidthPx(): number {
    return this.labelWidthMm * PX_PER_MM;
  }

  get naturalHeightPx(): number {
    return this.labelHeightMm * PX_PER_MM;
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) {
        this.scale.set(w / this.naturalWidthPx);
        this.cdr.markForCheck();
      }
    });
    this.resizeObserver.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:pointermove', ['$event'])
  onGlobalPointerMove(event: PointerEvent): void {
    if (!this.dragSession || !this.editable || event.pointerId !== this.dragSession.pointerId) {
      return;
    }

    const pointer = this.toMmPoint(event.clientX, event.clientY);
    const dx = pointer.x - this.dragSession.startPointer.x;
    const dy = pointer.y - this.dragSession.startPointer.y;
    let next =
      this.dragSession.mode === 'move'
        ? this.moveBounds(this.dragSession.startBounds, dx, dy)
        : this.resizeBounds(
            this.dragSession.startBounds,
            this.dragSession.handle!,
            dx,
            dy,
            this.dragSession.elementId === 'code',
          );

    // For code elements, enforce the natural aspect ratio (QR=1:1, Barcode≈3.33:1)
    if (this.dragSession.elementId === 'code' && this.dragSession.mode === 'resize') {
      next = this.enforceCodeAspectRatio(next, this.dragSession.startBounds);
    }

    this.emitPatch(this.dragSession.elementId, this.clampBounds(next));
    event.preventDefault();
  }

  @HostListener('window:pointerup', ['$event'])
  onGlobalPointerUp(event: PointerEvent): void {
    if (!this.dragSession || event.pointerId !== this.dragSession.pointerId) {
      return;
    }
    this.dragSession = null;
  }

  @HostListener('window:pointercancel', ['$event'])
  onGlobalPointerCancel(event: PointerEvent): void {
    if (!this.dragSession || event.pointerId !== this.dragSession.pointerId) {
      return;
    }
    this.dragSession = null;
  }

  onElementPointerDown(event: PointerEvent, elementId: EditableElementId): void {
    if (!this.editable) {
      return;
    }
    const bounds = this.getBoundsForElement(elementId);
    this.selectedElement.set(elementId);
    this.dragSession = {
      pointerId: event.pointerId,
      elementId,
      mode: 'move',
      startPointer: this.toMmPoint(event.clientX, event.clientY),
      startBounds: bounds,
    };
    this.capturePointer(event);
    event.preventDefault();
    event.stopPropagation();
  }

  onResizeHandlePointerDown(
    event: PointerEvent,
    elementId: EditableElementId,
    handle: ResizeHandle,
  ): void {
    if (!this.editable) {
      return;
    }
    const bounds = this.getBoundsForElement(elementId);
    this.selectedElement.set(elementId);
    this.dragSession = {
      pointerId: event.pointerId,
      elementId,
      mode: 'resize',
      handle,
      startPointer: this.toMmPoint(event.clientX, event.clientY),
      startBounds: bounds,
    };
    this.capturePointer(event);
    event.preventDefault();
    event.stopPropagation();
  }

  onCanvasPointerDown(): void {
    if (!this.editable) {
      return;
    }
    this.selectedElement.set(null);
  }

  isSelected(elementId: EditableElementId): boolean {
    return this.selectedElement() === elementId;
  }

  setHovered(elementId: EditableElementId | null): void {
    if (!this.editable) {
      return;
    }
    this.hoveredElement.set(elementId);
  }

  getElementStyle(elementId: EditableElementId): Record<string, string> {
    const bounds = this.getBoundsForElement(elementId);
    return {
      left: `${bounds.x}mm`,
      top: `${bounds.y}mm`,
      width: `${bounds.width}mm`,
      height: `${bounds.height}mm`,
    };
  }

  getTextStyle(field: 'checkpointName' | 'formName' | 'serialNumber'): Record<string, string> {
    const region = this.getTextRegion(field);
    const bounds = this.getBoundsForElement(field);
    const text = this.getTextContentByField(field);
    const ptSize = this.computeTextSizePt(
      text,
      bounds,
      region?.maxLines ?? 2,
      region?.fontSize ?? 8,
    );
    const style: Record<string, string> = {
      left: `${bounds.x}mm`,
      top: `${bounds.y}mm`,
      width: `${bounds.width}mm`,
      height: `${bounds.height}mm`,
      'text-align': region?.align ?? 'left',
      'font-weight': region?.fontWeight ?? 'normal',
      'font-size': `${ptSize}pt`,
      'font-family': this.getBrowserFont(),
    };
    if (this.debug) {
      const w = Math.ceil(1.5 / this.scale());
      style['outline'] = `${w}px solid rgba(220,130,50,0.9)`;
    }
    return style;
  }

  getTextContentByField(field: 'checkpointName' | 'formName' | 'serialNumber'): string {
    switch (field) {
      case 'checkpointName':
        if (!this.showCheckpointName) return '';
        return this.checkpointName || 'Checkpoint';
      case 'formName':
        if (!this.showFormName) return '';
        return this.formName || 'Form';
      case 'serialNumber':
        if (!this.showSerialNumber) return '';
        return this.serialNumber || '#1';
      default:
        return '';
    }
  }

  getHandleList(): ResizeHandle[] {
    return ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  }

  private getBrowserFont(): string {
    switch (this.fontFamily) {
      case 'times':
        return "'Times New Roman', Times, serif";
      case 'courier':
        return "'Courier New', Courier, monospace";
      default:
        return 'Arial, Helvetica, sans-serif';
    }
  }

  get qrStyle(): Record<string, string> {
    const bounds = this.getBoundsForElement('code');
    const style: Record<string, string> = {
      left: `${bounds.x}mm`,
      top: `${bounds.y}mm`,
      width: `${bounds.width}mm`,
      height: `${bounds.height}mm`,
    };
    if (this.debug) {
      const w = Math.ceil(1.5 / this.scale());
      style['outline'] = `${w}px solid rgba(50,180,50,0.9)`;
    }
    return style;
  }

  get checkpointNameVisible(): boolean {
    return this.showCheckpointName;
  }

  get formNameVisible(): boolean {
    return this.showFormName;
  }

  get serialNumberVisible(): boolean {
    return this.showSerialNumber;
  }

  get usableAreaStyle(): Record<string, string> | null {
    if (!this.debug) return null;
    const pad = this.layout.layoutConfig.padding;
    const usableW = this.labelWidthMm - pad.left - pad.right;
    const usableH = this.labelHeightMm - pad.top - pad.bottom;
    const w = Math.ceil(1.5 / this.scale());
    return {
      position: 'absolute',
      left: `${pad.left}mm`,
      top: `${pad.top}mm`,
      width: `${usableW}mm`,
      height: `${usableH}mm`,
      outline: `${w}px solid rgba(50,100,220,0.9)`,
      'pointer-events': 'none',
    };
  }

  private getBoundsForElement(elementId: EditableElementId): BoundsMm {
    const pad = this.layout.layoutConfig.padding;
    const usableW = this.labelWidthMm - pad.left - pad.right;
    const usableH = this.labelHeightMm - pad.top - pad.bottom;

    if (elementId === 'code') {
      const qr = this.layout.layoutConfig.qr;
      return this.clampBounds({
        x: this.qrX ?? pad.left + qr.x * usableW,
        y: this.qrY ?? pad.top + qr.y * usableH,
        width: this.qrWidth ?? qr.width * usableW,
        height: this.qrHeight ?? qr.height * usableH,
      });
    }

    const region = this.getTextRegion(elementId);
    const fallback = this.getSerialFallbackRegion();
    const source = region ?? (elementId === 'serialNumber' ? fallback : null);

    if (!source) {
      return this.clampBounds({ x: pad.left, y: pad.top, width: usableW, height: usableH * 0.25 });
    }

    const override =
      elementId === 'checkpointName'
        ? {
            x: this.checkpointNameX,
            y: this.checkpointNameY,
            width: this.checkpointNameWidth,
            height: this.checkpointNameHeight,
          }
        : elementId === 'formName'
          ? {
              x: this.formNameX,
              y: this.formNameY,
              width: this.formNameWidth,
              height: this.formNameHeight,
            }
          : {
              x: this.serialNumberX,
              y: this.serialNumberY,
              width: this.serialNumberWidth,
              height: this.serialNumberHeight,
            };

    return this.clampBounds({
      x: override.x ?? pad.left + source.x * usableW,
      y: override.y ?? pad.top + source.y * usableH,
      width: override.width ?? source.width * usableW,
      height: override.height ?? source.height * usableH,
    });
  }

  private getTextRegion(
    field: 'checkpointName' | 'formName' | 'serialNumber',
  ): LabelTextRegion | null {
    return this.layout.layoutConfig.textRegions.find((r) => r.field === field) ?? null;
  }

  private getSerialFallbackRegion(): LabelTextRegion {
    return {
      field: 'serialNumber',
      x: 0.38,
      y: 0.75,
      width: 0.62,
      height: 0.25,
      fontSize: 8,
      fontWeight: 'normal',
      align: 'left',
      maxLines: 1,
    };
  }

  private computeTextSizePt(
    text: string,
    bounds: BoundsMm,
    maxLines: number,
    fallbackSize: number,
  ): number {
    if (!text.trim()) {
      return fallbackSize;
    }

    const widthMm = Math.max(1, bounds.width);
    const heightMm = Math.max(1, bounds.height);
    const lineLimit = Math.max(1, maxLines);
    const chars = Math.max(1, text.length);

    const fits = (fontSizePt: number): boolean => {
      const charWidthMm = fontSizePt * MM_PER_PT * 0.56;
      const charsPerLine = Math.max(1, Math.floor(widthMm / charWidthMm));
      const lines = Math.ceil(chars / charsPerLine);
      const textHeightMm = lines * fontSizePt * MM_PER_PT * TEXT_LINE_HEIGHT;
      return lines <= lineLimit && textHeightMm <= heightMm;
    };

    let low = 4;
    let high = 48;
    let best = 4;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (fits(mid)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return best;
  }

  private clampBounds(bounds: BoundsMm): BoundsMm {
    const width = Math.min(this.labelWidthMm, Math.max(this.minSizeMm, bounds.width));
    const height = Math.min(this.labelHeightMm, Math.max(this.minSizeMm, bounds.height));
    const x = Math.min(this.labelWidthMm - width, Math.max(0, bounds.x));
    const y = Math.min(this.labelHeightMm - height, Math.max(0, bounds.y));
    return {
      x: Number(x.toFixed(3)),
      y: Number(y.toFixed(3)),
      width: Number(width.toFixed(3)),
      height: Number(height.toFixed(3)),
    };
  }

  private moveBounds(bounds: BoundsMm, dx: number, dy: number): BoundsMm {
    return {
      ...bounds,
      x: bounds.x + dx,
      y: bounds.y + dy,
    };
  }

  private resizeBounds(
    start: BoundsMm,
    handle: ResizeHandle,
    dx: number,
    dy: number,
    preserveAspect: boolean,
  ): BoundsMm {
    const right = start.x + start.width;
    const bottom = start.y + start.height;
    let next: BoundsMm = { ...start };

    if (handle.includes('e')) {
      next.width = start.width + dx;
    }
    if (handle.includes('s')) {
      next.height = start.height + dy;
    }
    if (handle.includes('w')) {
      next.x = start.x + dx;
      next.width = start.width - dx;
    }
    if (handle.includes('n')) {
      next.y = start.y + dy;
      next.height = start.height - dy;
    }

    if (preserveAspect) {
      const ratio = start.width / Math.max(start.height, 0.001);
      if (handle === 'n' || handle === 's') {
        next.width = next.height * ratio;
      } else if (handle === 'e' || handle === 'w') {
        next.height = next.width / ratio;
      } else {
        next.height = next.width / ratio;
      }

      if (handle.includes('w')) {
        next.x = right - next.width;
      }
      if (handle.includes('n')) {
        next.y = bottom - next.height;
      }
      if (handle.includes('e')) {
        next.x = handle.includes('w') ? next.x : start.x;
      }
      if (handle.includes('s')) {
        next.y = handle.includes('n') ? next.y : start.y;
      }
    }

    return next;
  }

  private enforceCodeAspectRatio(bounds: BoundsMm, originalBounds: BoundsMm): BoundsMm {
    // QR code: 1:1 aspect ratio (24x24 viewBox)
    // Barcode: ~3.33:1 aspect ratio (80x24 viewBox)
    const targetRatio = this.codeType === 'qr' ? 1 : 80 / 24;
    const currentRatio = bounds.width / Math.max(bounds.height, 0.001);

    // Adjust dimensions to match target aspect ratio
    if (Math.abs(currentRatio - targetRatio) > 0.01) {
      // If width grew more than height, adjust height
      if (currentRatio > targetRatio) {
        bounds.height = bounds.width / targetRatio;
      } else {
        // If height grew more than width, adjust width
        bounds.width = bounds.height * targetRatio;
      }
    }

    return bounds;
  }

  private toMmPoint(clientX: number, clientY: number): { x: number; y: number } {
    const natural = this.el.nativeElement.querySelector('.label-natural') as HTMLElement | null;
    if (!natural) {
      return { x: 0, y: 0 };
    }
    const rect = natural.getBoundingClientRect();
    const xPx = (clientX - rect.left) / this.scale();
    const yPx = (clientY - rect.top) / this.scale();
    return {
      x: Number((xPx / PX_PER_MM).toFixed(3)),
      y: Number((yPx / PX_PER_MM).toFixed(3)),
    };
  }

  private capturePointer(event: PointerEvent): void {
    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
  }

  private emitPatch(elementId: EditableElementId, bounds: BoundsMm): void {
    if (elementId === 'code') {
      this.boundsChange.emit({
        qrX: bounds.x,
        qrY: bounds.y,
        qrWidth: bounds.width,
        qrHeight: bounds.height,
      });
      return;
    }

    const text = this.getTextContentByField(elementId);
    const region = this.getTextRegion(elementId) ?? this.getSerialFallbackRegion();
    const computedSize = this.computeTextSizePt(
      text,
      bounds,
      region.maxLines ?? 2,
      region.fontSize ?? 8,
    );
    if (elementId === 'checkpointName') {
      this.boundsChange.emit({
        checkpointNameX: bounds.x,
        checkpointNameY: bounds.y,
        checkpointNameWidth: bounds.width,
        checkpointNameHeight: bounds.height,
        checkpointNameFontSize: computedSize,
      });
      return;
    }
    if (elementId === 'formName') {
      this.boundsChange.emit({
        formNameX: bounds.x,
        formNameY: bounds.y,
        formNameWidth: bounds.width,
        formNameHeight: bounds.height,
        formNameFontSize: computedSize,
      });
      return;
    }
    this.boundsChange.emit({
      serialNumberX: bounds.x,
      serialNumberY: bounds.y,
      serialNumberWidth: bounds.width,
      serialNumberHeight: bounds.height,
      serialNumberFontSize: computedSize,
    });
  }
}
